import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normalizeLearningLanguageCode } from '../constants/learningLanguages';
import { useLanguageLearning } from '../contexts/LanguageLearningContext';

// `languageCode` pins the learning language for routes without a /:lang segment
// (e.g. /learn-english), matching what /en/learn-english did.
const LanguageRouteWrapper = ({ children, languageCode }) => {
  const { lang: routeLang } = useParams();
  const lang = languageCode || routeLang;
  const { t } = useTranslation('common');
  const { activeLanguage, setActiveLanguage, availableLanguages, loading, ready } = useLanguageLearning();
  // ready covers auth and the signed-in profile; loading alone flips before the profile arrives.
  const isReady = ready ?? !loading;
  const normalizedRouteLanguage = normalizeLearningLanguageCode(lang, '');
  const hasSupportedRouteLanguage = Boolean(
    normalizedRouteLanguage
    && availableLanguages.some((language) => language.code === normalizedRouteLanguage)
  );

  useEffect(() => {
    if (isReady && hasSupportedRouteLanguage && normalizedRouteLanguage !== activeLanguage) {
      setActiveLanguage(normalizedRouteLanguage);
    }
  }, [activeLanguage, hasSupportedRouteLanguage, isReady, normalizedRouteLanguage, setActiveLanguage]);

  if (!isReady || availableLanguages.length === 0) {
    return <div>{t('loading', 'Loading...')}</div>;
  }

  if (lang && !hasSupportedRouteLanguage) {
    return <Navigate to={`/en${window.location.pathname.substring(3)}`} replace />;
  }

  if (hasSupportedRouteLanguage && normalizedRouteLanguage !== activeLanguage) {
    return <div>{t('loading', 'Loading...')}</div>;
  }

  return children;
};

export default LanguageRouteWrapper;
